import { ApolloLink, HttpLink, type Operation } from "@apollo/client";
import { setContext } from "@apollo/client/link/context";
import { createUploadLink } from "apollo-upload-client"; // terminal apollo link
import { BatchHttpLink } from "@apollo/client/link/batch-http";
import { RetryLink } from "@apollo/client/link/retry";
import { getCSRFToken } from "common/utils/CSRFToken";
import errorLink from "common/helpers/graphql/errorLink";

const baseHeaders = {
  "X-Requested-With": "XMLHttpRequest",
  "graphql-client-version": __JSQ_COMMIT_HASH__,
};

const isUpload = (node: unknown): boolean => {
  // adapted from https://github.com/jaydenseric/apollo-upload-client/issues/63#issuecomment-392501449
  if (typeof Blob !== "undefined" && node instanceof Blob) {
    return true;
  }
  if (typeof node !== "object" || node === null) {
    return false;
  }
  return Object.values(node).some(isUpload);
};

const isAsync = (context: Record<string, unknown>): boolean => {
  return context.doNotBatch === true;
};

const useUploadLink = (operation: Operation): boolean => {
  return isUpload(operation.variables) || isAsync(operation.getContext());
};

// ref: https://www.apollographql.com/docs/react/api/link/apollo-link-retry#default-configuration
const retryLink = new RetryLink({
  delay: {
    initial: Number(process.env.INITIAL_APOLLO_DELAY) || 1000,
    max: Number(process.env.MAX_APOLLO_DELAY) || Infinity,
    jitter: Boolean(process.env.APOLLO_JITTER) || true,
  },
  attempts: {
    max: Number(process.env.MAX_APOLLO_ATTEMPTS) || 3,
    retryIf: (error, _operation) => !!error,
  },
});

const isOutlookRequest = (operation: Operation) => operation.getContext().outlookUserToken;

const outlookLink = new ApolloLink((operation, forward) => {
  if (isOutlookRequest(operation)) {
    const sessionToken = localStorage.getItem("jsqOutlookSessionToken");
    const sessionArena = localStorage.getItem("jsqOutlookSessionArena");
    const isNaaSupported = localStorage.getItem("outlookAuthIsNaaSupported");
    operation.setContext(() => ({
      headers: {
        "JSQ-OUTLOOK-USER": operation.getContext().outlookUserToken,
        "JSQ-OUTLOOK-SESSION": sessionToken,
        "JSQ-OUTLOOK-SESSION-ARENA": sessionArena,
        "JSQ-OUTLOOK-AUTH-NAA-SUPPORTED": isNaaSupported,
      },
    }));
  }
  return forward(operation);
});

const csrfMiddleware = setContext(async () => {
  return {
    headers: {
      "X-CSRF-TOKEN": await getCSRFToken(),
    },
  };
});

interface LinkOptions {
  // Identifies this link's traffic in WunderGraph Cosmo Studio's Client Usage panel.
  // Named graphqlClientName (not clientName) to stay distinct from the unrelated
  // operation.getContext().clientName routing hint used below by controlTowerLink.
  graphqlClientName: string;
  includeOutlookLink?: boolean;
  useTerminatingBatchLink?: boolean;
}

export const getLink = (
  uri: string,
  { graphqlClientName, includeOutlookLink = true, useTerminatingBatchLink = true }: LinkOptions,
): ApolloLink => {
  const commonOptions = {
    uri,
    headers: { ...baseHeaders, "graphql-client-name": graphqlClientName },
    credentials: "same-origin",
  };

  const terminatingLink =
    useTerminatingBatchLink && process.env.NODE_ENV !== "test"
      ? new BatchHttpLink(commonOptions)
      : new HttpLink(commonOptions);

  const terminatingLinkWithErrorHandling = errorLink.split(
    (operation) => useUploadLink(operation),
    createUploadLink(commonOptions),
    terminatingLink,
  );

  const baseLinks = [csrfMiddleware, retryLink];
  if (includeOutlookLink) {
    baseLinks.push(outlookLink);
  }
  baseLinks.push(terminatingLinkWithErrorHandling);

  return ApolloLink.from(baseLinks);
};

export enum FetchPolicy {
  CacheAndNetwork = "cache-and-network",
  CacheFirst = "cache-first",
  NetworkOnly = "network-only",
  NoCache = "no-cache",
}
